import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import "./AnnouncementBar.css";

const STORAGE_KEY  = "unicx_studio_bar_dismissed_v1";
const BAR_HEIGHT_VAR = "--topbar-current-height";
const MIN_REVEAL_DELAY = 2500;

const PHASES = { hidden: 0, mounted: 1, ready: 2, revealed: 3, closing: 4, gone: 5 };

function AnnouncementBar({ digitalDomainUrl = "https://studio.unicx.in/" }) {
    const [phase, setPhase]   = useState(PHASES.hidden);
    const barRef    = useRef(null);
    const spacerRef = useRef(null);
    const rafRef    = useRef(null);
    const mountTime = useRef(Date.now());

    const syncHeight = useCallback((h) => {
        document.documentElement.style.setProperty(BAR_HEIGHT_VAR, `${h}px`);
        if (spacerRef.current) spacerRef.current.style.height = `${h}px`;
    }, []);

    useLayoutEffect(() => {
        if (localStorage.getItem(STORAGE_KEY) === "true") {
            syncHeight(0);
        }
    }, [syncHeight]);

    useEffect(() => {
        if (localStorage.getItem(STORAGE_KEY) === "true") return;

        let cancelled = false;

        const doReveal = () => {
            if (cancelled) return;
            setPhase(PHASES.mounted);
            rafRef.current = requestAnimationFrame(() => {
                if (cancelled) return;
                if (barRef.current) syncHeight(barRef.current.offsetHeight);
                rafRef.current = requestAnimationFrame(() => {
                    if (!cancelled) setPhase(PHASES.revealed);
                });
            });
        };

        const onIdle = () => {
            const elapsed   = Date.now() - mountTime.current;
            const remaining = Math.max(0, MIN_REVEAL_DELAY - elapsed);
            const t = setTimeout(doReveal, remaining);
            return () => clearTimeout(t);
        };

        let cleanup;
        if (typeof window !== "undefined" && "requestIdleCallback" in window) {
            const id = window.requestIdleCallback(onIdle, { timeout: MIN_REVEAL_DELAY + 1500 });
            cleanup = () => window.cancelIdleCallback(id);
        } else {
            const id = setTimeout(doReveal, MIN_REVEAL_DELAY);
            cleanup = () => clearTimeout(id);
        }

        return () => {
            cancelled = true;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            cleanup?.();
        };
    }, [syncHeight]);

    useEffect(() => {
        if (phase < PHASES.mounted || phase >= PHASES.gone) return;
        if (!barRef.current) return;
        const ro = new ResizeObserver(() => {
            if (barRef.current) syncHeight(barRef.current.offsetHeight);
        });
        ro.observe(barRef.current);
        return () => ro.disconnect();
    }, [phase, syncHeight]);

    const handleClose = useCallback(() => {
        if (phase >= PHASES.closing) return;
        setPhase(PHASES.closing);
        syncHeight(0);
        localStorage.setItem(STORAGE_KEY, "true");
        setTimeout(() => setPhase(PHASES.gone), 300);
    }, [phase, syncHeight]);

    const handleMouseMove = useCallback((e) => {
        if (barRef.current) {
            const rect = barRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const pct = (x / rect.width) * 100;
            barRef.current.style.setProperty("--mouse-x", `${x}px`);
            barRef.current.style.setProperty("--mouse-y", `${y}px`);
            barRef.current.style.setProperty("--mouse-x-percent", `${pct}%`);
        }
    }, []);


    const isInDOM   = phase >= PHASES.mounted && phase < PHASES.gone;
    const phaseClass = phase === PHASES.revealed
        ? "p-topbar--revealed"
        : phase === PHASES.closing
            ? "p-topbar--closing"
            : "";

    return (
        <>
            <div ref={spacerRef} className="p-topbar-spacer-block" aria-hidden="true" />

            {isInDOM && (
                <div
                    ref={barRef}
                    id="unicx-studio-topbar"
                    className={`p-topbar-container ${phaseClass}`}
                    role="banner"
                    aria-label="UniCX Studio announcement"
                    onMouseMove={handleMouseMove}
                >
                    {/* ── LEFT: live status indicator ── */}
                    <div className="p-topbar-left" aria-hidden="true">
                        <div className="p-topbar-dot-wrapper">
                            <span className="p-topbar-dot" />
                            <span className="p-topbar-pulse-ring" />
                            <span className="p-topbar-pulse-ring-outer" />
                        </div>
                        <span className="p-topbar-live-label">Live</span>
                    </div>

                    {/* ── CENTER: logo · divider · copy · link ── */}
                    <div className="p-topbar-content">
                        <div className="p-topbar-logo-container">
                            <img
                                src="https://studio.unicx.in/images/optimized/SU10.webp"
                                alt="UniCX Studio"
                                className="p-topbar-logo"
                                loading="eager"
                                draggable="false"
                            />
                        </div>

                        <span className="p-topbar-divider" aria-hidden="true" />

                        <span className="p-topbar-text">
                            Crafting digital products for brands worth remembering.
                        </span>

                        <a
                            href={digitalDomainUrl}
                            id="unicx-studio-topbar-cta"
                            className="p-topbar-cta-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Explore UniCX Studio"
                        >
                            <span>Explore Studio</span>
                            <svg
                                className="p-arrow-icon"
                                viewBox="0 0 24 24"
                                width="11"
                                height="11"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </a>
                    </div>

                    {/* ── RIGHT: close ── */}
                    <div className="p-topbar-right">
                        <button
                            id="unicx-studio-topbar-close"
                            className="p-topbar-close-btn"
                            onClick={handleClose}
                            aria-label="Dismiss announcement"
                            type="button"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="12"
                                height="12"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                fill="none"
                                strokeLinecap="round"
                                aria-hidden="true"
                            >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default AnnouncementBar;
